import type { SupabaseClient } from "@supabase/supabase-js";

import { fetchBrandProfiles, type BrandRow } from "../data/brand";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

// White-label brand profile surface (read-only preview) — the brand profiles configured for an
// account (display name, theme, colors, active/default state). No mutating controls; editing lives
// elsewhere. Backed by public.management_brand_profile.
export function BrandProfile({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
}) {
  const { status, data, error } = useReadModel<BrandRow[]>(
    () => fetchBrandProfiles(client, accountId),
    [client, accountId],
    pollMs,
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading brand profiles…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load brand profiles."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return <p className="surface__state" role="status">No brand profiles configured.</p>;
  }

  return (
    <ul className="brand-profiles">
      {rows.map((row, index) => (
        <li key={row.brandProfileId || `${row.brandKey ?? "brand"}-${index}`} className="brand-profile">
          <div className="brand-profile__top">
            <span className="brand-profile__name">{row.displayName ?? "Untitled brand"}</span>
            <span className="brand-profile__flags">
              {row.isDefault ? <span className="brand-profile__flag">Default</span> : null}
              <span className={`brand-profile__flag brand-profile__flag--${row.active ? "active" : "inactive"}`}>
                {row.active ? "Active" : "Inactive"}
              </span>
            </span>
          </div>
          <dl className="brand-profile__meta">
            {row.brandKey !== null ? (
              <div className="brand-profile__field">
                <dt>Key</dt>
                <dd>{row.brandKey}</dd>
              </div>
            ) : null}
            {row.themeKey !== null ? (
              <div className="brand-profile__field">
                <dt>Theme</dt>
                <dd>{row.themeKey}</dd>
              </div>
            ) : null}
            {row.primaryColor !== null ? (
              <div className="brand-profile__field">
                <dt>Primary</dt>
                <dd>
                  <span
                    className="brand-profile__swatch"
                    style={{ backgroundColor: row.primaryColor }}
                    aria-hidden="true"
                  />
                  {row.primaryColor}
                </dd>
              </div>
            ) : null}
            {row.accentColor !== null ? (
              <div className="brand-profile__field">
                <dt>Accent</dt>
                <dd>
                  <span
                    className="brand-profile__swatch"
                    style={{ backgroundColor: row.accentColor }}
                    aria-hidden="true"
                  />
                  {row.accentColor}
                </dd>
              </div>
            ) : null}
          </dl>
          <div className="brand-profile__when">Updated {formatDisplayTime(row.updatedAt, timeZone)}</div>
        </li>
      ))}
    </ul>
  );
}
